import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dimmer, Grid, Loader, Segment } from 'semantic-ui-react';
import { isEqual } from 'lodash';

// Import actions.
import {
  fetchFA2FormDetail,
  fa2GuideSelected,
  fa2PerformanceMeasureSelected,
  fetchFA2Submission,
  fetchFA2FormSchema,
  fa2ChildFileSave,
  clearFA2Forms,
} from '../../../actions/fa2Actions';
import { fetchNotes } from '../../../actions/noteActions';
import { formatUserNotes, generateTagFilter } from '../../Notes/util.js';
import { has, filter, find, isEmpty, map, remove, uniqBy } from 'lodash';

import SlidingContainer from '../../../utils/layout/SlidingContainer';
import NotesContainer from '../../Notes';
import GranteeInfo from './FA2GranteeInfo';
import FA2Guides from './FA2Guides';
import FA2PerformanceMeasures from './FA2PerformanceMeasures';

import FA2Form from './FA2Form';
import AmsAlert from '../../../utils/AmsAlert';
import AmsDateFormatters from '../../../utils/AmsDateFormatters';
import AmsTable from '../../../utils/AmsTable';
import FA2ChildFiles from './FA2ChildFiles';
import FA2CenterClasssroom from './FA2CenterClassroom';
import FindingsOutsideProtocol from './FA2FindingsOutsideProtocol';
import { postFormSubmission } from '../../../actions/surveyActions';
import { fetchReviewOCISummary } from '../../../actions/fy19/reviewActions';
import { fetchReviewDetail } from '../../../actions/reviewActions';
import FA2CRFindingsOutsideProtocol from '../FA2CRSurvey/FindingOutsideProtocol';
import FA2CRPerformanceMeasures from '../FA2CRSurvey/FA2CRPerformanceMeasures';

import '../assets/style.css';

export default function FA2Survey(props) {
  const dispatch = useDispatch();
  const {
    reviewType,
    selectedGuide,
    selectedPerformanceMeasure,
    isReportFinalized,
    reviewId,
    submission,
    selectedCenter,
    selectedClassroom,
  } = useSelector(state => state.forms.fa2);
  const { notes } = useSelector(state => state.note);
  const { selectedReview } = useSelector(state => state.review);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState({});
  const [surveyNotes, setSurveyNotes] = useState([]);
  const [options, setOptions] = useState([]);
  const [formData, setFormData] = useState({});
  const [dataHasChanged, setDataHasChanged] = useState(false);
  const [showMovingAlert, setShowMovingAlert] = useState(false);
  const [movingTo, setMovingTo] = useState({
    pm: '',
    guide: '',
    center: '',
    classRoom: '',
  });
  const [childFileSubmission, setChildFileSubmission] = useState({});
  const amsTableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [amsFormId, setAmsFormId] = useState('');
  const [selectedPM, setSelectedPM] = useState({});
  const [childFileErrors, setChildFileErrors] = useState({});
  const [originalData, setOriginalData] = useState({});

  // useEffect to fetch the form detail and selected review
  useEffect(() => {
    let id = props.params && props.params.reviewId && props.params.reviewId;
    if (id) {
      if (!has(selectedReview, 'id') || selectedReview.id === '') {
        dispatch(fetchReviewDetail({ reviewId: id }))
          .then(() => {
            dispatch(fetchFA2FormDetail({ reviewId: id }));
          })
          .catch(error => {
            console.log(error);
          });
      } else {
        dispatch(fetchFA2FormDetail({ reviewId: id }));
      }
    }
    return () => {
      dispatch(clearFA2Forms());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect to preselect the guide and performance measure from url params
  useEffect(() => {
    if (reviewId) preselect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, JSON.stringify(props.location.query || '')]);

  // useEffect to set title for different review types
  useEffect(() => {
    if (reviewType) {
      const title =
        reviewType && reviewType === 'Special'
          ? 'Special Survey'
          : reviewType && reviewType === 'AIAN-DEF Reeval'
          ? 'AIAN-DEF Reeval Survey'
          : reviewType && reviewType === 'ERSEA'
          ? 'ERSEA Survey'
          : reviewType && reviewType === 'FA2-CR'
          ? 'FA2-CR Survey'
          : 'FA-2 Survey';
      setTitle(title);
    }
  }, [reviewType]);

  // useEffect to construct tag options for notes
  useEffect(() => {
    if (
      notes &&
      !isEmpty(selectedGuide) &&
      !isEmpty(selectedPerformanceMeasure)
    ) {
      const pmOptions =
        selectedGuide.performanceMeasures.map(pm => {
          return {
            key: pm.name,
            text: pm.name,
            value: pm.name,
          };
        }) || [];
      setSurveyNotes(formatUserNotes(notes));
      setTags({
        type: 'survey_collection',
        reviewId,
        guide: selectedGuide.name,
        performanceMeasure: selectedPerformanceMeasure.name,
      });
      setOptions(
        generateTagFilter([
          ...[
            { key: 'review', value: '', text: 'All Notes for this Review' },
            { key: 'guide', value: 'guide', text: 'All Notes for this Guide' },
            { key: 'divider', value: 'divider', text: '' },
          ],
          ...pmOptions,
        ])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // useEffect to fetch notes when the selected guide or pm changes
  useEffect(() => {
    if (selectedGuide && selectedPerformanceMeasure) {
      let id = props.params && props.params.reviewId && props.params.reviewId;
      const body = {
        filters: {
          tags: {
            reviewId: id,
          },
        },
      };
      dispatch(fetchNotes(body));
      setSelectedPM(selectedPerformanceMeasure);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuide, selectedPerformanceMeasure]);

  // useEffect to fetch form schema and oci summary when the pm changes
  useEffect(() => {
    if (
      selectedPM &&
      selectedPM.name &&
      selectedPM.name === 'Content Area Analysis'
    ) {
      dispatch(
        fetchReviewOCISummary({
          reviewId: reviewId,
          reviewType: 'FA-2',
        })
      ).then(() => fetchFormSchema());
    } else {
      fetchFormSchema();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPM.name)]);

  // useEffect to fetch form schema when selected pm changes
  useEffect(() => {
    if (selectedPM && !selectedPM.formSchema) fetchFormSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPM.formSchema)]);

  /**
   * Fetch form survey submission
   * @param {string} amsSubmissionId
   */
  const fetchSurveyData = amsSubmissionId => {
    if (amsSubmissionId)
      dispatch(
        fetchFA2Submission({
          filters: {
            amsSubmissionId: amsSubmissionId,
          },
        })
      ).then(response => {
        setLoading(false);
        if (response.submission) {
          setFormData(response.submission.data.surveyData);
          setOriginalData(response.submission.data.surveyData);
          setChildFileSubmission({});
        }
      });
    else {
      setLoading(false);
      setFormData({});
    }
  };

  /**
   * Fetch form schema
   */
  const fetchFormSchema = () => {
    const { forms, hasCenters, centers } = selectedPerformanceMeasure;
    let amsFormId = '';
    let amsSubmissionId = '';
    setLoading(true);

    if (hasCenters) {
      var center = centers?.find(x => x.centerName === movingTo?.center);
      if (center && center.forms && center.forms[0].amsFormId) {
        amsFormId = center.forms[0].amsFormId;
        amsSubmissionId = center.forms[0].amsSubmissionId;
      } else if (centers && centers[0].forms) {
        amsFormId =
          centers &&
          centers[0] &&
          centers[0].forms &&
          centers[0].forms[0].amsFormId;
        amsSubmissionId =
          centers &&
          centers[0] &&
          centers[0].forms &&
          centers[0].forms[0].amsSubmissionId;
      } else {
        let centerToUse;
        if (!centers && centers.length <= 0) return;

        if (centers && movingTo?.center)
          centerToUse = centers?.find(x => x.centerName === movingTo?.center);
        if (!centerToUse) centerToUse = centers[0];

        var classRoom = centerToUse.classrooms.find(
          x => x.classSampleId === movingTo?.classRoom
        );
        if (classRoom && classRoom.forms && classRoom.forms[0]) {
          amsFormId = classRoom.forms[0].amsFormId;
          amsSubmissionId = classRoom.forms[0].amsSubmissionId;
        } else {
          amsFormId =
            centers &&
            centerToUse &&
            centerToUse.classrooms &&
            centerToUse.classrooms[0].forms &&
            centerToUse.classrooms[0].forms[0].amsFormId;
          amsSubmissionId =
            centers &&
            centerToUse &&
            centerToUse.classrooms &&
            centerToUse.classrooms[0].forms &&
            centerToUse.classrooms[0].forms[0].amsSubmissionId;
        }
      }
    } else {
      amsFormId = forms && forms[0] && forms[0].amsFormId;
      amsSubmissionId = forms && forms[0] && forms[0].amsSubmissionId;
    }
    if (amsFormId) {
      setAmsFormId(amsFormId);
      dispatch(fetchFA2FormSchema({ filters: { amsFormId } })).then(
        response => {
          if (response) {
            fetchSurveyData(amsSubmissionId);
          }
        }
      );
    } else {
      // TO DO: Display something when form id is not present
      setLoading(false);
    }
  };

  /**
   * Preselect the guide and pm
   */
  const preselect = () => {
    // Extract query strings if passed and use them to preselect Guide and PM.
    const guideName = !isEmpty(props.location.query.g)
      ? decodeURIComponent(props.location.query.g)
      : '';
    const performanceMeasure = !isEmpty(props.location.query.pm)
      ? decodeURIComponent(props.location.query.pm)
      : '';
    dispatch(fa2GuideSelected(guideName));
    dispatch(fa2PerformanceMeasureSelected(performanceMeasure));
  };

  /**
   * Set the guide and pm on redux store
   * @param {Object} guide
   */
  const changeGuide = guide => {
    setLoading(true);
    if (dataHasChanged && !showMovingAlert && checkDifference()) {
      setMovingTo({ guide: guide });
      setShowMovingAlert(true);
      return;
    }
    setFormData({});
    dispatch(fetchFA2FormDetail({ reviewId })).then(() => {
      dispatch(fa2GuideSelected(guide));
      dispatch(fa2PerformanceMeasureSelected(guide.performanceMeasures[0]));
      setLoading(false);
    });
    props.router.push(
      `${props.location.pathname}?g=${encodeURIComponent(
        guide.name
      )}&pm=${encodeURIComponent(guide.performanceMeasures[0].name)}`
    );
  };

  const checkDifference = () => {
    if (isEqual({}, formData) || isEqual(formData, originalData)) {
      return false;
    }
    return true;
  };

  /**
   * Set the pm and guide on redux store
   * @param {Object} performanceMeasure
   * @param {Object} selectedGuide
   * @returns {void}
   */
  const changePerformanceMeasure = (performanceMeasure, selectedGuide) => {
    setLoading(true);
    if (dataHasChanged && !showMovingAlert && checkDifference()) {
      setMovingTo({ pm: performanceMeasure, guide: selectedGuide });
      setShowMovingAlert(true);
      return;
    }
    setFormData({});
    dispatch(fetchFA2FormDetail({ reviewId })).then(() => {
      dispatch(fa2GuideSelected(selectedGuide));
      dispatch(fa2PerformanceMeasureSelected(performanceMeasure));
      setLoading(false);
    });
    props.router.push(
      `${props.location.pathname}?g=${encodeURIComponent(
        selectedGuide.name
      )}&pm=${encodeURIComponent(performanceMeasure.name)}`
    );
  };

  /**
   * Set the form data changes for submission
   * @param {Object} formData
   * @returns {void}
   */
  const handleDataChange = ({ formData }) => {
    if (Object.values(formData).every(v => v === undefined)) {
      setFormData({});
      return;
    }
    setFormData(formData);
    if (checkDifference()) {
      setDataHasChanged(true);
    }
  };

  /**
   * Handle child file deletion for form submission
   * @param {Object} data
   * @returns {void}
   */
  const handleChildFileDelete = data => {
    setChildFileSubmission({
      delete: [...data.delete],
      update: [...data.update],
      create: [...data.create],
    });
    setFormData({
      ...formData,
      topQuestion: formData.topQuestion,
      aianQuestion: formData.aianQuestion,
      childFiles: remove(
        formData.childFiles,
        file => file.childId !== data.selectedChildFile.childId
      ),
    });
    setDataHasChanged(true);
  };

  const handleCenterAndClassChange = (guide, pm, center, classRoom) => {
    setMovingTo({ pm: pm, guide: guide, center, center, classRoom: classRoom });
    setLoading(true);
    if (dataHasChanged && !showMovingAlert && checkDifference()) {
      setShowMovingAlert(true);
      return false;
    }
    return true;
  };

  /**
   * Handle child file data changes for form submission
   * @param {Object} data
   * @param {string} type - top question or child file
   * @returns {void}
   */
  const handleChildFileDataChange = (data, type = '') => {
    if (type === 'topQuestion') {
      setFormData({
        ...formData,
        topQuestion: data.topQuestion,
      });
      setDataHasChanged(true);
      return;
    }

    if (type === 'aianQuestion') {
      setFormData({
        ...formData,
        aianQuestion: data.aianQuestion,
      });
      setDataHasChanged(true);
      return;
    }

    if (!data.rowClicked && !data.fileEditButtonClicked) {
      let created = [...(childFileSubmission.create || [])];
      created.push(data.formData);
      setChildFileSubmission({
        ...childFileSubmission,
        create: [...created],
      });
      setFormData({
        ...formData,
        topQuestion: formData.topQuestion,
        aianQuestion: formData.aianQuestion,
        childFiles: uniqBy(
          [...(created || []), ...(formData.childFiles || [])],
          'childId'
        ),
      });
    } else {
      if (
        find(
          childFileSubmission.create,
          file => file.childId === data.previousChildId
        )
      ) {
        let created = map(childFileSubmission.create, file => {
          if (file.childId === data.previousChildId)
            file = { ...data.formData };
          return file;
        });
        setChildFileSubmission({
          ...childFileSubmission,
          create: [...created],
        });
        setFormData({
          ...formData,
          topQuestion: formData.topQuestion,
          aianQuestion: formData.aianQuestion,
          childFiles: map(formData.childFiles, file => {
            if (file.childId === data.previousChildId)
              file = {
                ...data.formData,
                mongoId: data.selectedChildFile.mongoId,
              };
            return file;
          }),
        });
      } else if (
        find(
          childFileSubmission.update,
          file => file.childId === data.previousChildId
        )
      ) {
        let updated = map(childFileSubmission.update, file => {
          if (file.childId === data.previousChildId)
            file = {
              ...data.formData,
              mongoId: data.selectedChildFile.mongoId,
            };
          return file;
        });
        setChildFileSubmission({
          ...childFileSubmission,
          update: [...updated],
        });
        setFormData({
          ...formData,
          topQuestion: formData.topQuestion,
          aianQuestion: formData.aianQuestion,
          childFiles: map(formData.childFiles, file => {
            if (file.childId === data.previousChildId)
              file = {
                ...data.formData,
                mongoId: data.selectedChildFile.mongoId,
              };
            return file;
          }),
        });
      } else {
        let updated = childFileSubmission.update
          ? [...childFileSubmission.update]
          : [];
        updated.push({
          ...data.formData,
          mongoId: data.selectedChildFile.mongoId,
        });
        setChildFileSubmission({
          ...childFileSubmission,
          update: [...updated],
        });
        setFormData({
          ...formData,
          topQuestion: formData.topQuestion,
          aianQuestion: formData.aianQuestion,
          childFiles: map(formData.childFiles, file => {
            if (file.childId === data.previousChildId)
              file = {
                ...data.formData,
                mongoId: data.selectedChildFile.mongoId,
              };
            return file;
          }),
        });
      }
    }

    setDataHasChanged(true);
  };

  /**
   * Keep track of the form data changes and cancel moving to another page
   * @param {Object} formData
   */
  const handleCancelChange = formData => {
    setFormData(formData);
    setDataHasChanged(false);
  };

  /**
   * Display the moving alert modal when the data has changed
   * @returns {void}
   */
  const renderMovingAlert = () => {
    const { surveyStatus } = submission;
    return (
      <AmsAlert
        show={showMovingAlert}
        title="Are you sure you want to move away from this form"
        text={`You selected another page, are you sure you want to leave the current page?`}
        type={'warning'}
        confirmButtonColor={'#112e51'}
        confirmButtonText={
          surveyStatus && surveyStatus.toLowerCase() === 'submitted'
            ? 'Submit and continue'
            : 'Save draft and continue'
        }
        cancelButtonText={'Continue without saving'}
        showConfirm
        showCancelButton
        showThirdButton
        thirdButtonText="Cancel"
        onThirdButtonAction={() => {
          setShowMovingAlert(false);
          setLoading(false);
        }}
        onCancel={() => {
          setChildFileSubmission({});
          if (movingTo.pm)
            changePerformanceMeasure(movingTo.pm, movingTo.guide);
          else changeGuide(movingTo.guide);
          setShowMovingAlert(false);
          setDataHasChanged(false);
        }}
        onConfirm={() => {
          setShowMovingAlert(false);
          if (
            selectedPerformanceMeasure.name ===
              'Determining, verifying, and documenting eligibility (Eligibility Files)' ||
            selectedPerformanceMeasure.name ===
              'Eligibility File Review - Individual File Data' ||
            selectedPerformanceMeasure.name ===
              'Determining, Verifying, and Documenting Eligibility (Eligibility Files)' ||
            selectedPerformanceMeasure.name ===
              'Determining, verifying, and documenting eligibility (Child Files)' ||
            selectedPerformanceMeasure.name ===
              'Determining, Verifying, and Documenting Eligibility (Child Files)'
          ) {
            handleChildFileSubmit();
          } else handleSubmit();
        }}
      />
    );
  };

  /**
   * Handle child file submission for the form data
   * @returns {void}
   */
  const handleChildFileSubmit = () => {
    let data = {
      create: childFileSubmission.create || [],
      update: childFileSubmission.update || [],
      delete:
        filter(childFileSubmission.delete, childFile => childFile.mongoId) ||
        [],
      topQuestion: formData.topQuestion,
      aianQuestion: formData.aianQuestion,
      surveyStatus:
        !isEmpty(submission.surveyStatus) &&
        submission.surveyStatus.toLowerCase() === 'submitted'
          ? 'submitted'
          : 'draft',
    };
    if (submission && submission.amsSubmissionId)
      data.amsSubmissionId = submission.amsSubmissionId;
    if (selectedPM.forms && selectedPM.forms[0].amsFormId)
      data.amsFormId = selectedPM.forms[0].amsFormId;
    if (reviewId) data.reviewId = reviewId;
    if (selectedPM.formCode) data.formCode = selectedPM.formCode;
    if (selectedGuide.guideCode) data.guideCode = selectedGuide.guideCode;
    dispatch(fa2ChildFileSave({ data }))
      .then(response => {
        if (response) {
          console.log('success');
        }
        dataHasChangedSwitch(false);
        dispatch(fetchFA2FormDetail({ reviewId })).then(() => {
          if (movingTo.pm)
            changePerformanceMeasure(movingTo.pm, movingTo.guide);
          else changeGuide(movingTo.guide);
          setLoading(false);
          setChildFileErrors({});
        });
      })
      .catch(error => {
        let errorData =
          !isEmpty(error.response.data.message) &&
          JSON.parse(error.response.data.message) &&
          !isEmpty(JSON.parse(error.response.data.message).text) &&
          JSON.parse(error.response.data.message);
        setChildFileErrors(errorData);
        setLoading(false);
      });
  };

  /**
   * Submit the form data
   * @returns {void}
   */
  const handleSubmit = () => {
    const { forms, hasCenters, centers } = selectedPerformanceMeasure;
    let amsFormId = '';
    if (hasCenters) {
      var center = centers?.find(x => x.centerName === movingTo?.center);
      if (center && center.forms && center.forms[0].amsFormId) {
        amsFormId = center.forms[0].amsFormId;
      } else if (centers && centers[0].forms) {
        amsFormId =
          centers &&
          centers[0] &&
          centers[0].forms &&
          centers[0].forms[0].amsFormId;
      } else {
        let centerToUse;
        if (!centers && centers.length <= 0) return;

        if (centers && movingTo?.center)
          centerToUse = centers?.find(x => x.centerName === movingTo?.center);
        if (!centerToUse) centerToUse = centers[0];

        if (centerToUse) {
          var classRoom = centerToUse.classrooms.find(
            x => x.classSampleId === movingTo?.classRoom
          );
          if (classRoom && classRoom.forms && classRoom.forms[0])
            amsFormId = classRoom.forms[0].amsFormId;
          else {
            amsFormId =
              centerToUse &&
              centerToUse.classrooms &&
              centerToUse.classrooms[0].forms &&
              centerToUse.classrooms[0].forms[0].amsFormId;
          }
        }
      }
    } else amsFormId = forms && forms[0] && forms[0].amsFormId;

    if (!amsFormId || !reviewId) return;

    let requestObject = { reviewId, amsFormId, surveyData: formData };

    if (formData.topQuestion === false) {
      requestObject = {
        reviewId,
        amsFormId,
        surveyData: { topQuestion: false },
      };
    }

    if (formData.aianQuestion === false) {
      requestObject = {
        reviewId,
        amsFormId,
        surveyData: { aianQuestion: false },
      };
    }

    if (submission.amsSubmissionId)
      requestObject.amsSubmissionId = submission.amsSubmissionId;

    if (selectedCenter) requestObject.centerName = selectedCenter;

    if (selectedClassroom) requestObject.classSampleId = selectedClassroom;

    // draft
    if (
      isEmpty(submission) ||
      (submission.surveyStatus &&
        submission.surveyStatus.toLowerCase() === 'draft')
    ) {
      dispatch(
        postFormSubmission({
          data: { ...requestObject, submissionStatus: 'Draft' },
        })
      )
        .then(response => {
          dataHasChangedSwitch(false);
          dispatch(fetchFA2FormDetail({ reviewId })).then(() => {
            if (movingTo.pm)
              changePerformanceMeasure(movingTo.pm, movingTo.guide);
            else changeGuide(movingTo.guide);
            setLoading(false);
          });
        })
        .catch(error => {
          console.log(error);
        });
    } else {
      // update
      if (
        submission.surveyStatus &&
        submission.surveyStatus.toLowerCase() === 'submitted'
      ) {
        dispatch(
          postFormSubmission({
            data: { ...requestObject, submissionStatus: 'Submitted' },
          })
        )
          .then(response => {
            dataHasChangedSwitch(false);
            dispatch(fetchFA2FormDetail({ reviewId })).then(() => {
              if (movingTo.pm)
                changePerformanceMeasure(movingTo.pm, movingTo.guide);
              else changeGuide(movingTo.guide);
              setLoading(false);
            });
          })
          .catch(error => {
            console.log(error);
          });
        return; // End of update exit.
      }
    }
  };

  /**
   * Render the version table for the submissions
   * @returns {void}
   */
  const renderVersionHistoryTable = () => {
    const data = submission.versionHistory.sort((a, b) =>
      AmsDateFormatters.getMoment(b.modifiedDate).diff(
        AmsDateFormatters.getMoment(a.modifiedDate)
      )
    );

    const linkVersion = (cell, row) => {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          href=""
          onClick={e => {
            e.preventDefault();
            const amsSubmissionId = submission.amsSubmissionId;
            const version = cell;

            // Fetch specific version.
            dispatch(
              fetchFA2Submission({
                filters: { amsSubmissionId, version },
              })
            ).then(response => {
              if (response.submission)
                setFormData(response.submission.data.surveyData);
            });
          }}
        >
          {cell}
        </a>
      );
    };

    const formatDate = (cell, row) => AmsDateFormatters.formatDateTime(cell);

    const columns = [
      {
        dataField: 'version',
        text: 'Version',
        sort: true,
        formatter: linkVersion,
        style: { whiteSpace: 'normal' },
      },
      {
        dataField: 'editedByFullName',
        text: 'Edited By',
        sort: true,
        style: { whiteSpace: 'normal' },
      },
      {
        dataField: 'editTime',
        sort: true,
        formatter: formatDate,
        style: { whiteSpace: 'normal' },
        text: 'Edited Time',
      },
    ];
    return (
      <AmsTable
        remote={false}
        basic
        total={data.length}
        columns={columns}
        keyField="version"
        ref={amsTableRef}
        data={data}
      />
    );
  };

  // Switch to control dataHasChanged state
  const dataHasChangedSwitch = value => {
    setDataHasChanged(value);
  };
  const newFormReviewTypes = ['Special', 'AIAN-DEF Reeval'];
  return (
    <SlidingContainer
      calendar
      granteeStatusBanner={true}
      granteeStatusData={{
        type: 'review',
      }}
      title={`${title} for Review ID ${reviewId}`}
      actionButtons={
        <NotesContainer
          tags={tags && tags}
          defaultFilter={'performanceMeasure'}
          filterOptions={options && options}
          list={surveyNotes && surveyNotes}
          enabled={!isReportFinalized && surveyNotes}
          type={'Notes'}
          reloadData={() => {
            const body = {
              filters: {
                tags: {
                  reviewId,
                },
              },
            };
            dispatch(fetchNotes(body));
          }}
        />
      }
    >
      {dataHasChanged && renderMovingAlert()}
      <Dimmer active={loading} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column>{<GranteeInfo />}</Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={4}>
            <FA2Guides {...props} changeGuide={changeGuide} />
            {reviewType === 'FA2-CR' ? (
              <FA2CRPerformanceMeasures
                changePerformanceMeasure={changePerformanceMeasure}
              />
            ) : (
              <FA2PerformanceMeasures
                changePerformanceMeasure={changePerformanceMeasure}
              />
            )}
          </Grid.Column>

          <Grid.Column stretched width={12}>
            {!isEmpty(selectedPerformanceMeasure) &&
              (selectedPerformanceMeasure.name ===
                'Determining, verifying, and documenting eligibility (Eligibility Files)' ||
              selectedPerformanceMeasure.name ===
                'Determining, Verifying, and Documenting Eligibility (Eligibility Files)' ||
              selectedPerformanceMeasure.name ===
                'Eligibility File Review - Individual File Data' ||
              selectedPerformanceMeasure.name ===
                'Determining, verifying, and documenting eligibility (Child Files)' ||
              selectedPerformanceMeasure.name ===
                'Determining, Verifying, and Documenting Eligibility (Child Files)' ? (
                <FA2ChildFiles
                  handleDataChange={handleChildFileDataChange}
                  formData={formData}
                  childFileSubmission={childFileSubmission}
                  fetchSurveyData={id => fetchSurveyData(id)}
                  dataHasChangedSwitch={dataHasChangedSwitch}
                  amsFormId={amsFormId}
                  handleChildFileDelete={handleChildFileDelete}
                  childFileSaveErrors={childFileErrors}
                  handleCancelChange={handleCancelChange}
                />
              ) : selectedPerformanceMeasure.name ===
                'Findings Outside the Protocol' ? (
                reviewType === 'FA2-CR' ||
                (newFormReviewTypes.includes(selectedReview.reviewType) &&
                  selectedReview?.isNewForms) ? (
                  <FA2CRFindingsOutsideProtocol
                    formData={formData}
                    handleDataChange={handleDataChange}
                    dataHasChangedSwitch={dataHasChangedSwitch}
                    handleCancelChange={handleCancelChange}
                  />
                ) : (
                  <FindingsOutsideProtocol
                    formData={formData}
                    handleDataChange={handleDataChange}
                    dataHasChangedSwitch={dataHasChangedSwitch}
                    handleCancelChange={handleCancelChange}
                  />
                )
              ) : (
                <Segment basic>
                  <FA2CenterClasssroom
                    {...props}
                    center={movingTo?.center}
                    classRoom={movingTo?.classRoom}
                    setLoading={value => setLoading(value)}
                    fetchSurveyData={id => fetchSurveyData(id)}
                    handleCenterAndClassChange={handleCenterAndClassChange}
                  />
                  <FA2Form
                    handleDataChange={handleDataChange}
                    formData={formData}
                    dataHasChangedSwitch={dataHasChangedSwitch}
                    handleCancelChange={handleCancelChange}
                    amsFormId={amsFormId}
                  />
                </Segment>
              ))}
            {submission &&
              submission.versionHistory &&
              renderVersionHistoryTable()}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </SlidingContainer>
  );
}
